// VIS-206: the map's start, and the one place a failed start is caught (index.html loads only this module).
// 1. errors.js first (a static import), so it sees every failure from here on.
// 2. A cheap probe: a browser with neither WebGPU nor WebGL2 cannot draw the map; main.js is then never fetched.
// 3. main.js by a dynamic import: every failure of its start (a file that did not arrive, a renderer that could not
//    start, any throw in its top-level awaits) rejects this one import.
// 4. A watchdog: 45 s without a 'map-progress' event before the map is ready = 'timeout'. Not fatal: the start goes
//    on, and the loader's message goes away when the map turns ready. VIS-257: only time someone can see the map
//    counts (a hidden tab or a frame scrolled out of view pauses the wait), and the report names the last step reached.
// 5. VIS-27: a start that took more than SLOW_MS of seen time is reported once as a warning ('slow map start').
// Each failure becomes a 'map-failed' event { reason } that js/loader.js shows, and one report to Sentry (area 'boot',
// tag reason; extras: step, progress, seen_s, hidden_s, slowest_step). A lost graphics device after the start comes
// from main.js as the same event; errors.js reports that.
// The loader answers with 'map-retry' (Try again: a fresh page) or 'map-wait' (Keep waiting: the watchdog restarts).
// Reasons: no-3d | network | timeout | error (| device-lost, main.js).
// VIS-267: before anything starts, a previous start that died without a word (rescue.js takeUnfinished) counts as a
// loss (the safer path, one warning); the 3D probe asks for a real WebGL2 context and, without one, a WebGPU adapter;
// a build that dies of a lost device (a graphics error rejects the import) takes the lost-device way out
// (rescue.js lostAction: one reload onto the safer path, or the box). The stored failure belongs to the release it
// was made on (rescue.js readLost, 2026-10-25): the box never outlives a release, so stopped() is asked with RELEASE.
import { reportError, reportWarning, RELEASE, flush, markGpuLost, releaseCheck } from './errors.js';
import { stopped, allowTry, hasWebGL2, takeUnfinished, liveStarts, countLoss, lostAction, computerDevice, appleMobile, isGpuError, firstLoss, ownStart, endStart } from './rescue.js'; // VIS-255: a device whose rescue set failed too opens on the box, not on a 3D start

export const WATCHDOG_MS = 45000;

/**
 * Why the start failed, from what the import of main.js rejected with.
 * @param {unknown} err
 * @returns {'no-3d' | 'network' | 'error'}
 */
export function bootReason(err) {
  if (err && typeof err === 'object' && /** @type {any} */ (err).mapReason === 'no-3d') return 'no-3d'; // main.js: renderer.init() threw
  const msg = String(/** @type {any} */ (err)?.message ?? err ?? '');
  if (/getSupportedExtensions/.test(msg)) return 'no-3d'; // VIS-267: three's WebGL2 backend on a null context (VIEWER-J: WebGPU failed, WebGL2 blocked)
  // a module or file that did not arrive: Chrome, Firefox, Safari wordings of a failed import or fetch, and config.js's
  // own "<file>: <status>"
  if (/dynamically imported module|Importing a module script failed|Failed to fetch|NetworkError|Load failed|network error|: \d{3}$/i.test(msg)) return 'network';
  return 'error';
}

/**
 * Can this browser draw 3D at all? VIS-267: a real check. A WebGL2 context on a throwaway canvas (released at once),
 * else a WebGPU adapter: `navigator.gpu` alone is no answer (Chromium keeps it after it blocked 3D for the page, and
 * then gives no adapter and a null WebGL2 context).
 * @param {{ gpu?: { requestAdapter?: (o?: object) => Promise<unknown> } }} nav  @param {() => { getContext: (kind: string) => unknown }} makeCanvas
 * @returns {Promise<boolean>}
 */
export async function can3d(nav, makeCanvas) {
  if (hasWebGL2(makeCanvas)) return true;
  try { return !!(await nav.gpu?.requestAdapter?.({ featureLevel: 'compatibility' })); } catch { return false; } // errors:ignore: an adapter request that throws = no WebGPU
}

const TIMERS = { set: (fn, n) => setTimeout(fn, n), clear: (id) => clearTimeout(id), now: () => Date.now() };

/**
 * The no-progress watchdog: `onTimeout` once `ms` pass without a poke(); stop() ends it for good. VIS-257: only time
 * someone can see the map counts: pause() (the tab hidden, the map's frame scrolled out of view: the browser then slows
 * or stops the loading) holds the wait where it is, resume() goes on with what is left of it.
 * @param {number} ms  @param {() => void} onTimeout  @param {{ set: typeof setTimeout, clear: typeof clearTimeout, now: () => number }} [t]
 */
export function createWatchdog(ms, onTimeout, t = TIMERS) {
  let id = null, done = false, paused = false, waiting = true, left = ms, since = 0;
  const fire = () => { waiting = false; if (!done) onTimeout(); };
  const arm = () => { t.clear(id); if (done || paused || !waiting) return; since = t.now(); id = t.set(fire, left); };
  arm();
  return {
    poke() { left = ms; waiting = true; arm(); },
    pause() { if (paused || done) return; paused = true; if (waiting) { left = Math.max(0, left - (t.now() - since)); t.clear(id); } },
    resume() { if (!paused) return; paused = false; arm(); },
    stop() { done = true; t.clear(id); },
  };
}

/**
 * VIS-257 / VIS-27: the start's own clock. It counts only seen time (pause/resume as the watchdog) and how long each
 * loading step (the words of main.js's 'map-progress') took of it, so a report says where a start got stuck or slow.
 * @param {() => number} [now]
 */
export function createStartClock(now = TIMERS.now) {
  const t0 = now();
  let seen = 0, since = t0, running = true, step = '', p = 0, stepAt = 0;
  const steps = new Map();
  const ms = () => seen + (running ? now() - since : 0);
  const close = () => { if (step) steps.set(step, (steps.get(step) || 0) + ms() - stepAt); };
  return {
    pause() { if (running) { seen += now() - since; running = false; } },
    resume() { if (!running) { since = now(); running = true; } },
    progress(msg, at) { if (typeof at === 'number') p = at; if (!msg || msg === step) return; close(); step = msg; stepAt = ms(); },
    /** What a report says: the last step reached, how far (0..1), seconds seen, seconds hidden, the slowest step. */
    facts() {
      const seenMs = ms(), all = new Map(steps);
      if (step) all.set(step, (all.get(step) || 0) + seenMs - stepAt);
      const slowest = [...all].sort((a, b) => b[1] - a[1])[0];
      return { step: step || 'before the first step', progress: Math.round(p * 100) / 100, seen_s: Math.round(seenMs / 1000), hidden_s: Math.round((now() - t0 - seenMs) / 1000), slowest_step: slowest ? `${slowest[0]} (${Math.round(slowest[1] / 1000)} s)` : '' };
    },
    /** VIS-267: the seen seconds of every step, one tag each, named by the step's last word ('Final check' -> s_check). */
    steps() {
      const out = {}, all = new Map(steps);
      if (step) all.set(step, (all.get(step) || 0) + ms() - stepAt);
      for (const [msg, t] of all) { const k = `s_${(msg.toLowerCase().match(/[a-z]+$/) || ['step'])[0]}`; out[k] = (out[k] || 0) + Math.round(t / 1000); }
      return out;
    },
  };
}

/** VIS-27: a start that took longer than this (seen time) is reported once, as a warning: before the guest gives up. */
export const SLOW_MS = 20000;
/** Seconds as a few buckets: one tag value per kind of slow, searchable in Sentry. */
export const slowBucket = (s) => (s < 30 ? '20-30 s' : s < 45 ? '30-45 s' : s < 90 ? '45-90 s' : 'over 90 s');

/**
 * VIS-257: can anyone see the page? `onChange(seen)` when the tab is hidden or shown, or when the page (a hotel's
 * frame, the website's map) scrolls out of or into the browser's view. A browser without IntersectionObserver
 * counts only the tab.
 * @param {(seen: boolean) => void} onChange
 */
function watchSight(onChange) {
  let off = false;
  const tell = () => onChange(!document.hidden && !off);
  document.addEventListener('visibilitychange', tell);
  if (typeof IntersectionObserver === 'function') new IntersectionObserver(([e]) => { off = !e.isIntersecting; tell(); }).observe(document.documentElement);
  tell();
}

function fail(reason, err, facts = {}) {
  if (reason !== 'timeout') endStart(); // VIS-267: a start that ended with a known reason is no silent loss
  const e = err instanceof Error ? err : new Error(`map start: ${reason}`);
  reportError(e, { area: 'boot', reason, ...facts });
  // VIS-195 (Sentry VIEWER-W/-E): a module of a release that is gone (the page was opened before a deploy, or a browser
  // kept the old page): errors.js reloads the page once onto the new release and reports nothing; the failure box shows
  // only when the page stays (any other failure: at once, the check answers without asking anything)
  releaseCheck(e).then((r) => { if (!r.reload) dispatchEvent(new CustomEvent('map-failed', { detail: { reason } })); });
}

/**
 * VIS-267: a build that died of a lost device (a graphics error rejected main.js's import) is a loss: the set and the
 * backend from the start record main.js keeps, one reload onto the safer path or the box. Without a record the
 * renderer never came up: a plain failed start. Returns false when it was not a loss.
 */
function buildLost(err, facts) {
  const rec = ownStart(); // this page's own (never another tab's)
  if (!rec?.set || !rec.backend || !isGpuError(err)) return false;
  if (!firstLoss()) return true; // main.js's guard took it already
  const local = store('localStorage'), session = store('sessionStorage');
  const act = lostAction({ set: rec.set, backend: rec.backend, release: RELEASE, local, session, computer: computerDevice(navigator.userAgent, navigator.maxTouchPoints), apple: appleMobile(navigator.userAgent, navigator.maxTouchPoints) });
  reportError(err, { area: 'device-lost', reason: 'build', set: rec.set, ...facts });
  markGpuLost();
  if (act === 'reload') flush(1500).then(() => dispatchEvent(new Event('map-retry'))); // the report leaves first
  else dispatchEvent(new CustomEvent('map-failed', { detail: { reason: 'device-lost' } }));
  return true;
}

/**
 * VIS-267: the previous start on this device that never finished (the tab killed, Safari's silent loss) counts as a
 * loss: this start takes the safer path (rescue.js startPath), and one warning says where it stopped. A start another
 * tab is still running (its Web Lock is held: liveStarts) is not one.
 */
async function unfinishedBefore() {
  const local = store('localStorage'), prev = takeUnfinished({ local, live: await liveStarts() });
  if (!prev) return;
  countLoss({ set: prev.set, backend: prev.backend, release: RELEASE, local });
  reportWarning(`previous start stopped at ${prev.step}`, { area: 'boot', step: prev.step, set: prev.set, backend: prev.backend });
}

/** The start. A website cover (js/loader.js, #loader[data-state="cover"]) holds it until the guest's click ('map-open'). */
async function start() {
  await unfinishedBefore();
  if (!(await can3d(navigator, () => document.createElement('canvas')))) fail('no-3d', new Error('no WebGPU adapter and no WebGL2'));
  else if (stoppedHere()) fail('device-lost', new Error('map start: stopped after the rescue set lost its graphics too')); // VIS-255 (rescue.js stopped)
  else {
    const clock = createStartClock();
    const dog = createWatchdog(WATCHDOG_MS, () => fail('timeout', undefined, clock.facts())); // VIS-257: where it got stuck
    watchSight((seen) => { if (seen) { clock.resume(); dog.resume(); } else { clock.pause(); dog.pause(); } });
    addEventListener('map-progress', (e) => { clock.progress(e.detail?.msg, e.detail?.p); dog.poke(); });
    addEventListener('map-wait', dog.poke); // the loader's "Keep waiting": another 45 s
    addEventListener('map-failed', (e) => { if (e.detail?.reason !== 'timeout') dog.stop(); }); // a hard failure needs no "taking longer" on top
    new MutationObserver((_, obs) => {
      if (!document.body.classList.contains('ready')) return;
      dog.stop(); obs.disconnect();
      const f = clock.facts(); // VIS-27: a slow start is reported, with its slowest step
      if (f.seen_s * 1000 >= SLOW_MS) reportWarning('slow map start', { area: 'boot', seconds: slowBucket(f.seen_s), ...f, ...clock.steps() }); // VIS-267: + every step's seconds
    }).observe(document.body, { attributes: true, attributeFilter: ['class'] });
    import('./main.js').catch((err) => { dog.stop(); const reason = bootReason(err); if (reason === 'error' && buildLost(err, clock.facts())) return; fail(reason, err, clock.facts()); });
  }
}

// VIS-255: the stored failure (rescue.js); storage that throws counts as none (rescue.js wraps every access)
const store = (name) => () => globalThis[name];
const stoppedHere = () => stopped({ param: new URLSearchParams(location.search).get('phoneset'), local: store('localStorage'), session: store('sessionStorage'), release: RELEASE, apple: appleMobile(navigator.userAgent, navigator.maxTouchPoints) }); // VIS-267: a memory of one older release does not stop this one; rescue.js point 8: an iPhone's rescue set lost on WebGPU starts once more, on WebGL2

export const CHECK_MS = 15 * 60 * 1000;

/**
 * VIS-231: a map saved whole on the device (the service worker web/sw.js at /m/_sw.js, scope /m/; it downloads the
 * packing list the host names with the boot field `pack`, only on a page the host offers whole). Privacy (owner
 * 2026-10-17): nothing is stored on a visitor's device until asked, so the worker is registered here ONLY on a
 * placement whose portal switch keeps the map (boot field `offline`: 'nightly' | 'pinned': a lobby screen, a demo
 * laptop), which then saves itself. A guest saves with the Save row of the (i) panel (lazy/save.js), which registers
 * it. Any other page only talks to a worker that is already there: `mms:check` at the start and every 15 minutes (a
 * lobby screen stays open for days) keeps a saved copy renewed and brings its new version; this page never registers
 * one. A screen reloads when the night's new version is in. Nothing of it shows on the map.
 * @param {(field: string) => string} meta  @param {Navigator} [nav]  @param {Location} [loc]  @param {typeof setInterval} [every]
 */
export function offline(meta, nav = navigator, loc = location, every = setInterval) {
  const sw = nav.serviceWorker, mode = meta('offline');
  if (!sw || !meta('pack')) return; // not a page the host offers whole
  let reg = null;
  const tell = (type, more) => (sw.controller || reg?.active)?.postMessage({ type, url: loc.href, ...more });
  const check = () => tell('mms:check');
  sw.addEventListener('message', (e) => { if (e.data?.type === 'mms:updated' && meta('kind') === 'screen') loc.reload(); }); // a screen shows the night's new version at once
  if (!/^(nightly|pinned)$/.test(mode)) {
    if (sw.controller) { check(); every(check, CHECK_MS); } // no worker = nothing was ever saved here
    return;
  }
  nav.storage?.persist?.().catch(() => {}); // errors:ignore: a browser that will not promise keeps the copy anyway, until space runs out
  // the switch: save (or resume a save that stopped) at every start, then check
  sw.register('/m/_sw.js', { scope: '/m/' })
    .then(() => sw.ready)
    .then((r) => { reg = r; tell('mms:save', { mode, kind: meta('kind') }); every(check, CHECK_MS); })
    .catch((err) => reportError(err, { area: 'boot', reason: 'offline' }));
}

/**
 * Save to this computer (owner's pick B, 2026-10-31): Chrome and Edge offer once to install a page as an app, often
 * before the (i) panel was ever opened; the offer is kept here (`mmsInstall`) for the Save press of lazy/save.js. Only on
 * a page the host offers whole (its manifest is there only then), never on a placement that keeps itself.
 * preventDefault: no banner of the browser's own (nothing about saving shows unasked); the address bar's icon stays.
 * @param {(field: string) => string} meta  @param {any} [win]
 */
export function keepInstall(meta, win = globalThis) {
  if (!meta('pack') || meta('offline')) return;
  const k = win.mmsInstall = { prompt: null, done: false };
  win.addEventListener('beforeinstallprompt', (e) => { e.preventDefault(); k.prompt = e; });
  win.addEventListener('appinstalled', () => { k.prompt = null; k.done = true; });
}

if (typeof document !== 'undefined' && document.getElementById('loader')) {
  const meta = (n) => document.querySelector(`meta[name="mapmystay-${n}"]`)?.content || '';
  const l = document.getElementById('loader');
  // "Try again" (js/loader.js): the page starts over. A module that failed to import stays failed in this page (the
  // module map keeps the failure) and main.js may have half run, so a fresh page is the one clean retry. A cover page
  // starts again past its cover (`open=1`): the guest already pressed it.
  addEventListener('map-retry', () => {
    if (l.dataset.state === 'failed' && l.dataset.reason === 'device-lost') allowTry({ session: store('sessionStorage') }); // VIS-255: the guest's own Try again on a lost device: one more start (main.js's automatic reload never comes from this box)
    if (!meta('cover')) return location.reload();
    const u = new URL(location.href); u.searchParams.set('open', '1'); location.replace(u.href);
  });
  offline(meta);
  keepInstall(meta);
  if (l.dataset.state === 'cover') addEventListener('map-open', start, { once: true }); // the website cover: the click starts it
  else start();
}
